import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { attentionNav } from '../lib/attentionRoute';
import type { View } from '../nav/navModel';
import { AttentionRow, Card, Money } from '../ui';
import {
  actOnAttention,
  attentionSnapshot,
  type AttentionItem,
  loadAttention,
  resetAttentionStore,
  subscribeAttention,
} from './attentionStore';
import { attentionDefaultOpen, attentionHeading, dueLabel } from './attentionView';

/**
 * The ONE money-ranked «کارهای امروز» worklist (UX-1). Extracted from DashboardScreen Panel-D so the
 * dashboard, the deals landing, and the cash calendar all render the SAME look, the SAME per-item nav, and the
 * SAME accountability actions — one authority, never a forked second copy (the FLOW-2 trap). It self-fetches
 * `/api/attention` (the ONE worst-first authority, `{ items: AttentionItem[] }`, already sorted by
 * rials×urgency server-side and gated by `dashboard.read` — that gate is unchanged and un-weakened here) so a
 * host screen only has to thread the three nav callbacks.
 *
 * The multi-scan fetch lives in a tiny module-level store (./attentionStore) with a short TTL and a
 * subscriber set: navigating deals↔cash reuses ONE scan, and an accountability action force-refetches every
 * mounted strip. That store is OWNER-AWARE — keyed on the logged-in username — so the cache can never survive
 * a login/logout (an SPA state change with no reload) and serve one user's server-gated worklist to the next.
 */

export type { AttentionItem };

/** Subscribe to the shared, owner-aware worklist store for the current session. The snapshot getter is keyed
 *  on `owner` so a stale scan from a previous user is never handed back — not even on the first render — and
 *  the effect drops the store on logout and (re)loads on login. */
function useAttention(owner: string | null): AttentionItem[] {
  const getSnap = useCallback(() => attentionSnapshot(owner), [owner]);
  const snap = useSyncExternalStore(subscribeAttention, getSnap);
  useEffect(() => {
    if (owner === null) {
      resetAttentionStore();
      return;
    }
    void loadAttention(owner);
  }, [owner]);
  return snap ?? [];
}

/**
 * @param variant  'card' = the dashboard's always-open Card (Panel-D, unchanged look); 'collapsible' = a
 *                 compact `<details>` for the deals / cash landings, so the strip never shoves the screen
 *                 down (it defaults open only when something is critical today, else stays folded).
 */
export function AttentionStrip({
  variant = 'card',
  onOpenDeal,
  onOpenCustomer,
  onNavigate,
}: {
  variant?: 'card' | 'collapsible';
  onOpenDeal?: ((id: number) => void) | undefined;
  onOpenCustomer?: ((customerId: number) => void) | undefined;
  onNavigate?: ((view: View, focusDealId?: number) => void) | undefined;
}) {
  const { user } = useAuth();
  const owner = user?.username ?? null;
  const attention = useAttention(owner);
  // One click routes to the record (deal/customer) or its owning view — the SAME logic as the bell, so
  // "worst" and "where does it go" mean the same everywhere. Parametrized by the threaded callbacks.
  const openAttention = (item: AttentionItem) => {
    const nav = attentionNav(item.target);
    if (nav.kind === 'deal') onOpenDeal?.(nav.id);
    else if (nav.kind === 'customer') onOpenCustomer?.(nav.id);
    else onNavigate?.(nav.view as View);
  };
  // Uncontrolled-by-default disclosure: honour the user's toggle once made, otherwise open only on a
  // critical day. Applies to the collapsible variant only.
  const [userOpen, setUserOpen] = useState<boolean | null>(null);

  // No session (or nothing today) → render nothing. Narrowing `owner` to a string here lets the
  // accountability actions below carry the session that owns the store.
  if (owner === null || attention.length === 0) return null;

  const heading = attentionHeading(attention);

  const list = (
    <ul className="flagged-list">
      {attention.map((item) => {
        const due = dueLabel(item.dueDays);
        return (
          <AttentionRow key={item.id} onActivate={() => openAttention(item)}>
            <div className="fi-top">
              <span className={`att-dot ${item.severity}`} aria-hidden="true" />
              <span className="fi-no">{item.title}</span>
              <span className="fi-margin num">
                <Money value={item.rialsAtStake} />
              </span>
            </div>
            <div className="fi-warnings">
              {item.body}
              {due ? <span className="muted"> · {due}</span> : null}
            </div>
            {/* Team accountability — stop clicks/Enter here from also triggering the row's navigation. */}
            <div
              className="att-actions"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            >
              {item.ack ? (
                <>
                  <span className="att-assignee">در دست پیگیری: {item.ack.actor}</span>
                  <button
                    type="button"
                    className="linklike"
                    onClick={() => void actOnAttention(owner, item.id, 'clear')}
                  >
                    لغو
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="linklike"
                  onClick={() => void actOnAttention(owner, item.id, 'ack')}
                >
                  من پیگیری می‌کنم
                </button>
              )}
              {/* BIDX-P7: «قیمت تأییدنشده» is NON-SNOOZABLE — the server exempts it from the snoozed
                  filter AND the snooze route rejects its id. Rendering the button anyway would be a
                  control that claims an effect the code refuses, so it is replaced by the plain reason. */}
              {item.kind === 'unpriced_receipt' ? (
                <span className="muted">تا ثبت قیمت به تعویق نمی‌افتد</span>
              ) : (
                <button
                  type="button"
                  className="linklike"
                  onClick={() => void actOnAttention(owner, item.id, 'snooze', 7)}
                >
                  یادآوری ۷ روز بعد
                </button>
              )}
            </div>
          </AttentionRow>
        );
      })}
    </ul>
  );

  if (variant === 'collapsible') {
    const isOpen = userOpen ?? attentionDefaultOpen(attention);
    return (
      <details
        className="attention-strip"
        open={isOpen}
        onToggle={(e) => setUserOpen(e.currentTarget.open)}
      >
        <summary className="attention-strip-summary">{heading}</summary>
        {list}
      </details>
    );
  }

  return <Card title={heading}>{list}</Card>;
}
